import type { ConversationRow } from "../api/types";
import { t } from "../i18n";
import type { InboxView } from "./inbox";
import { nextStepsLine } from "./nextSteps";

// Views whose every conversation has the same status: the row does not repeat it.
const VIEW_STATUS: Partial<Record<InboxView, string>> = {
  queue: "new",
  waiting_contact: "waiting_contact",
  snoozed: "snoozed",
  closed: "closed",
};

type RowFacts = Pick<
  ConversationRow,
  "status" | "priority" | "snoozedUntil" | "tags"
> &
  Partial<
    Pick<
      ConversationRow,
      "handler" | "handlerName" | "lastInboundAt" | "nextSteps"
    >
  >;

/**
 * The status a row of the list shows, or null when it would say nothing new. WhatsApp Web's rows
 * have two lines; ours add a third only when it carries news: "Em atendimento" is the working
 * state, a view's own status is in its tab, and "Em soneca até …" already says it is snoozed.
 */
export function rowStatus(view: InboxView, row: RowFacts): string | null {
  if (row.status === "active" || VIEW_STATUS[view] === row.status) return null;
  if (row.status === "snoozed" && row.snoozedUntil) return null;
  return row.status;
}

/** "há 35 min", "há 2 h", "há 3 d": since a moment, roughly. */
function since(iso: string, now: number): string {
  const minutes = Math.max(1, Math.round((now - Date.parse(iso)) / 60_000));
  if (minutes < 60) return t("inbox.ago_minutes", { n: minutes });
  const hours = Math.round(minutes / 60);
  return hours < 48
    ? t("inbox.ago_hours", { n: hours })
    : t("inbox.ago_days", { n: Math.round(hours / 24) });
}

/**
 * In Automação: who has the conversation and when the contact last wrote ("Com o fluxo
 * Boas-vindas · contato escreveu há 35 min"), so whoever is stuck with a flow shows (2026-10-26),
 * then what comes next ("retomada hoje às 18:45 · conclui em 31 h sem resposta", decision 0044).
 */
export function handlerLine(
  view: InboxView,
  row: RowFacts,
  now = Date.now(),
): string | null {
  if (view !== "automation") return null;
  if (row.handler !== "flow" && row.handler !== "assistant") return null;
  const who =
    row.handler === "flow"
      ? row.handlerName
        ? t("inbox.with_flow", { name: row.handlerName })
        : t("inbox.with_a_flow")
      : row.handlerName
        ? t("inbox.with_assistant", { name: row.handlerName })
        : t("inbox.with_an_assistant");
  const next = nextStepsLine(row.nextSteps, new Date(now));
  return [
    who,
    row.lastInboundAt
      ? t("inbox.contact_wrote", { when: since(row.lastInboundAt, now) })
      : null,
    next,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Whether the row needs its third line at all. */
export function hasRowLine(view: InboxView, row: RowFacts): boolean {
  return Boolean(
    handlerLine(view, row) ||
    rowStatus(view, row) ||
    row.priority !== "normal" ||
    row.snoozedUntil ||
    row.tags?.length,
  );
}
